import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, ArrowsClockwise, Robot, Sparkle, X, ClockCounterClockwise,
  Plus, Minus, Equals, Warning, CheckCircle, Spinner, CaretDown, CaretUp, Buildings
} from "@phosphor-icons/react";

const AGENT_META = {
  officer: { label: "Officer", color: "bg-orange-100 text-orange-800 border-orange-200" },
  hunter: { label: "Hunter", color: "bg-blue-100 text-blue-800 border-blue-200" },
  validator: { label: "Validator", color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  response_manager: { label: "Response Mgr", color: "bg-purple-100 text-purple-800 border-purple-200" },
  compiler: { label: "Compiler", color: "bg-slate-900 text-white border-slate-900" },
  communicator: { label: "Communicator", color: "bg-amber-100 text-amber-800 border-amber-200" },
};

function TraceList({ trace }) {
  if (!trace || !trace.length) return <div className="text-xs text-slate-400">No trace recorded</div>;
  return (
    <div className="border border-slate-200 divide-y divide-slate-100 bg-white">
      {trace.map((t, i) => {
        const m = AGENT_META[t.agent] || { label: t.agent, color: "bg-slate-100 text-slate-700 border-slate-200" };
        return (
          <div key={i} className="px-3 py-1.5 flex items-center gap-2 text-xs">
            <span className="font-mono text-slate-400 w-4">{i + 1}</span>
            <span className={`font-mono px-2 py-0.5 border ${m.color}`} style={{ minWidth: 120 }}>
              {m.label}
            </span>
            <span className="text-slate-600 flex-1 truncate">{t.summary}</span>
            <span className="font-mono text-slate-500">{t.latency_ms}ms</span>
            {t.input_tokens > 0 && (
              <span className="font-mono text-slate-500">{t.input_tokens}+{t.output_tokens}t</span>
            )}
            <span className="font-mono text-emerald-700 w-14 text-right">₹{(t.cost_inr || 0).toFixed(4)}</span>
          </div>
        );
      })}
    </div>
  );
}

function DeltaPill({ delta, unit = "", invertGood = false }) {
  const isPos = delta > 0, isNeg = delta < 0, isZero = delta === 0;
  const good = invertGood ? isNeg : isPos;
  const bad = invertGood ? isPos : isNeg;
  const color = isZero ? "text-slate-600 border-slate-200 bg-slate-50"
    : good ? "text-emerald-700 border-emerald-200 bg-emerald-50"
    : bad ? "text-orange-700 border-orange-200 bg-orange-50"
    : "text-slate-600 border-slate-200 bg-slate-50";
  const sign = delta > 0 ? "+" : "";
  return (
    <span className={`font-mono text-xs px-2 py-0.5 border ${color} inline-flex items-center gap-1`}>
      {isZero ? <Equals size={10} /> : isPos ? <Plus size={10} /> : <Minus size={10} />}
      {sign}{delta}{unit}
    </span>
  );
}

export default function GovernanceReplay() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modeFilter, setModeFilter] = useState("agentic"); // 'agentic' | 'single' | ''
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [replay, setReplay] = useState(null);
  const [replaying, setReplaying] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (modeFilter) params.mode = modeFilter;
      const { data } = await api.get("/query-logs", { params });
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [modeFilter]);

  const openDetail = async (id) => {
    setOpenId(id);
    setDetail(null);
    setReplay(null);
    try {
      const { data } = await api.get(`/query-logs/${id}`);
      setDetail(data);
    } catch {
      toast.error("Failed to load log detail");
    }
  };
  const close = () => {
    setOpenId(null);
    setDetail(null);
    setReplay(null);
  };

  const runReplay = async () => {
    if (!detail) return;
    setReplaying(true);
    setReplay(null);
    try {
      const { data } = await api.post(`/query-logs/${detail.id}/replay`);
      setReplay(data);
      toast.success("Replay complete");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Replay failed");
    } finally {
      setReplaying(false);
    }
  };

  const costTotals = useMemo(() => {
    const total = items.reduce((a, b) => a + (b.cost_inr || 0), 0);
    const agentic = items.filter((i) => i.mode === "agentic").length;
    return { total, agentic, single: items.length - agentic };
  }, [items]);

  return (
    <div className="space-y-8" data-testid="governance-page">
      <div>
        <div className="mono-label text-orange-600 mb-2">// audit · replay · diff</div>
        <h1 className="font-display text-4xl md:text-5xl text-slate-900 tracking-tight leading-tight">
          Governance Replay
        </h1>
        <p className="text-slate-600 mt-2 max-w-2xl">
          Every user query, its full agent trace, and the ability to re-run it against today's knowledge to see
          exactly what changed. Every factual/pricing claim from this system is defensible.
        </p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Metric label="Total queries" value={items.length} />
        <Metric label="Agent Mesh" value={costTotals.agentic} />
        <Metric label="Single-shot" value={costTotals.single} />
        <Metric label="Cumulative ₹" value={`₹${costTotals.total.toFixed(2)}`} />
      </div>

      {/* Filter */}
      <div className="border border-slate-200 bg-white p-3 flex items-center gap-2" data-testid="gov-filters">
        <span className="mono-label">Mode</span>
        {[
          { v: "", label: "All" },
          { v: "agentic", label: "Agent Mesh" },
          { v: "single", label: "Single-shot" },
        ].map((f) => (
          <button
            key={f.v}
            onClick={() => setModeFilter(f.v)}
            className={`font-mono text-xs px-3 py-1 border transition-all ${
              modeFilter === f.v
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-700 border-slate-200 hover:border-slate-900"
            }`}
            style={{ borderRadius: 2 }}
            data-testid={`gov-filter-${f.v || "all"}`}
          >
            {f.label}
          </button>
        ))}
        <button onClick={load} className="btn-sharp btn-secondary text-xs ml-auto" data-testid="gov-refresh">
          <ArrowsClockwise size={12} />
          Refresh
        </button>
      </div>

      {/* List */}
      <div className="border border-slate-200 bg-white">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="mono-label flex items-center gap-2">
            <ShieldCheck size={14} className="text-emerald-600" /> Query audit log
          </div>
          <span className="font-mono text-xs px-2 py-0.5 bg-slate-100 border border-slate-200">
            {items.length}
          </span>
        </div>
        {loading ? (
          <div className="p-12 text-center mono-label pulse-soft">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-slate-400 text-sm">No queries match this filter</div>
        ) : (
          <div data-testid="gov-list">
            {items.map((it) => (
              <button
                key={it.id}
                onClick={() => openDetail(it.id)}
                className="w-full text-left border-b border-slate-100 px-5 py-3 hover:bg-slate-50 transition-all flex items-start gap-3"
                data-testid={`gov-row-${it.id}`}
              >
                <div className="shrink-0 mono-label w-32">
                  {new Date(it.created_at).toLocaleString([], { year: "2-digit", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                </div>
                {it.mode === "agentic" ? (
                  <span className="shrink-0 font-mono text-xs px-2 py-0.5 bg-orange-600 text-white inline-flex items-center gap-1">
                    <Robot size={10} weight="fill" /> Agent
                  </span>
                ) : (
                  <span className="shrink-0 font-mono text-xs px-2 py-0.5 bg-slate-700 text-white">
                    single
                  </span>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-900 truncate">{it.query}</div>
                  <div className="mono-label truncate">
                    by {it.user_email} · {(it.citations?.length) || 0} citations · {it.latency_ms || 0}ms
                    {it.rating && <span className="text-orange-600"> · rated {it.rating}/5</span>}
                  </div>
                </div>
                <span className="shrink-0 font-mono text-xs px-2 py-0.5 border border-emerald-200 bg-emerald-50 text-emerald-800">
                  ₹{(it.cost_inr || 0).toFixed(4)}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Detail drawer */}
      {openId && (
        <div className="fixed inset-0 z-30 bg-slate-900/50 flex justify-end" onClick={close} data-testid="gov-drawer">
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-5xl bg-white h-full overflow-y-auto border-l border-slate-200"
          >
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <div className="mono-label text-orange-600">// audit detail</div>
                <div className="font-mono text-xs text-slate-500 mt-1">{openId}</div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={runReplay}
                  disabled={replaying || !detail}
                  className="btn-sharp btn-primary text-sm disabled:opacity-50"
                  data-testid="replay-btn"
                >
                  {replaying ? <Spinner size={14} className="animate-spin" /> : <ClockCounterClockwise size={14} weight="bold" />}
                  {replaying ? "Replaying…" : "Replay against today's KB"}
                </button>
                <button onClick={close} className="btn-sharp btn-ghost" data-testid="gov-drawer-close">
                  <X size={16} />
                </button>
              </div>
            </div>

            {!detail ? (
              <div className="p-12 text-center mono-label pulse-soft">Loading…</div>
            ) : (
              <div className="p-6 space-y-6">
                <div>
                  <div className="mono-label mb-2">Query</div>
                  <div className="border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 font-mono" data-testid="gov-query">
                    {detail.query}
                  </div>
                  <div className="mono-label mt-2">
                    Asked by <span className="text-slate-700">{detail.user_email}</span>
                    {detail.rating && <span className="text-orange-600"> · rated {detail.rating}/5</span>}
                    · {new Date(detail.created_at).toLocaleString()}
                  </div>
                </div>

                {/* Side-by-side */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <Side
                    title="Original"
                    icon={<Sparkle size={14} weight="fill" className="text-orange-400" />}
                    data={detail}
                    testid="side-original"
                  />
                  {replay ? (
                    <Side
                      title="Replay · today's KB"
                      icon={<ClockCounterClockwise size={14} weight="bold" className="text-orange-400" />}
                      data={replay.replay}
                      testid="side-replay"
                    />
                  ) : (
                    <div className="border border-dashed border-slate-300 bg-slate-50 px-5 py-8 text-center">
                      <ClockCounterClockwise size={24} className="text-slate-400 mx-auto mb-2" />
                      <div className="mono-label text-slate-500">Replay not yet run</div>
                      <div className="text-xs text-slate-500 mt-2">
                        Click "Replay against today's KB" above to re-run this query on the current knowledge base
                        and see what has changed.
                      </div>
                    </div>
                  )}
                </div>

                {/* Diff */}
                {replay && (
                  <div className="border border-slate-200 bg-white" data-testid="gov-diff">
                    <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
                      <div className="mono-label text-slate-900">Diff</div>
                      {replay.diff.answer_changed ? (
                        <span className="font-mono text-xs px-2 py-0.5 border border-orange-200 bg-orange-50 text-orange-800 inline-flex items-center gap-1">
                          <Warning size={10} weight="fill" /> answer changed
                        </span>
                      ) : (
                        <span className="font-mono text-xs px-2 py-0.5 border border-emerald-200 bg-emerald-50 text-emerald-800 inline-flex items-center gap-1">
                          <CheckCircle size={10} weight="fill" /> answer unchanged
                        </span>
                      )}
                    </div>
                    <div className="p-5 space-y-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="mono-label">cost</span>
                        <DeltaPill delta={replay.diff.cost_inr_delta} unit="₹" invertGood />
                        <span className="mono-label">latency</span>
                        <DeltaPill delta={replay.diff.latency_ms_delta} unit="ms" invertGood />
                        <span className="mono-label">tokens</span>
                        <DeltaPill delta={replay.diff.token_delta} unit="t" invertGood />
                      </div>
                      <CitationSetDiff
                        kept={replay.diff.citations_kept}
                        added={replay.diff.citations_added}
                        removed={replay.diff.citations_removed}
                        origCitations={detail.citations || []}
                        replayCitations={replay.replay.citations || []}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="border border-slate-200 bg-white p-4">
      <div className="mono-label">{label}</div>
      <div className="font-display text-2xl text-slate-900 mt-1">{value}</div>
    </div>
  );
}

function Side({ title, icon, data, testid }) {
  const [traceOpen, setTraceOpen] = useState(false);
  return (
    <div className="border border-slate-200 bg-white" data-testid={testid}>
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
        {icon}
        <div className="mono-label text-slate-900">{title}</div>
        <span className="ml-auto font-mono text-xs text-slate-500">
          {data.latency_ms}ms · {data.total_tokens || 0}t · ₹{(data.cost_inr || 0).toFixed(4)}
        </span>
      </div>
      <div className="p-4 space-y-3">
        <div className="text-sm text-slate-900 leading-relaxed whitespace-pre-wrap font-mono">
          {data.answer || "(no answer)"}
        </div>

        {(data.citations || []).length > 0 && (
          <div>
            <div className="mono-label mb-1">Citations ({(data.citations || []).length})</div>
            <div className="space-y-1">
              {data.citations.map((c, ci) => (
                <div key={ci} className="border border-slate-200 px-3 py-2 bg-slate-50 text-xs">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono px-1.5 py-0.5 bg-slate-900 text-white">[{ci + 1}]</span>
                    <Buildings size={12} className="text-slate-500" />
                    <span className="font-mono text-slate-700 truncate flex-1">{c.location_path}</span>
                    <span className="font-mono text-orange-600">score {Number(c.score).toFixed(2)}</span>
                  </div>
                  <div className="text-sm text-slate-900 font-medium">{c.title}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {data.trace && data.trace.length > 0 && (
          <div>
            <button onClick={() => setTraceOpen((o) => !o)} className="mono-label flex items-center gap-1 hover:text-slate-900">
              {traceOpen ? <CaretUp size={10} /> : <CaretDown size={10} />}
              Agent trace · {data.trace.length} steps
            </button>
            {traceOpen && <div className="mt-2"><TraceList trace={data.trace} /></div>}
          </div>
        )}
      </div>
    </div>
  );
}

function CitationSetDiff({ kept, added, removed, origCitations, replayCitations }) {
  const titleFor = (kid) => {
    const all = [...origCitations, ...replayCitations];
    return all.find((c) => c.knowledge_id === kid)?.title || kid.slice(0, 8);
  };
  return (
    <div className="space-y-2">
      <div className="mono-label">Citation set diff</div>
      <div className="text-xs space-y-1">
        {kept.length === 0 && added.length === 0 && removed.length === 0 && (
          <div className="text-slate-400">no citations in either run</div>
        )}
        {kept.map((kid) => (
          <div key={`k-${kid}`} className="flex items-center gap-2">
            <span className="font-mono text-xs px-1.5 py-0.5 bg-slate-100 border border-slate-200 text-slate-700 w-20 text-center">KEPT</span>
            <span className="font-mono text-slate-700 truncate">{titleFor(kid)}</span>
          </div>
        ))}
        {added.map((kid) => (
          <div key={`a-${kid}`} className="flex items-center gap-2">
            <span className="font-mono text-xs px-1.5 py-0.5 bg-emerald-600 text-white w-20 text-center">ADDED</span>
            <span className="font-mono text-emerald-900 truncate">{titleFor(kid)}</span>
          </div>
        ))}
        {removed.map((kid) => (
          <div key={`r-${kid}`} className="flex items-center gap-2">
            <span className="font-mono text-xs px-1.5 py-0.5 bg-orange-600 text-white w-20 text-center">REMOVED</span>
            <span className="font-mono text-orange-900 truncate">{titleFor(kid)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
